import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '../../components';
import api from '../../services/api';
import { formatCourseThumbnail, handleThumbnailError } from '../../utils/imageUtils';
import { formatShortDate, formatTime } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const StudentCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [courseClasses, setCourseClasses] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subData, coursesData] = await Promise.all([
          api.getMySubscription().catch(() => ({ subscription: null, isActive: false })),
          api.getCourses({ limit: '100' }).catch(() => ({ courses: [] })),
        ]);

        setSubscription(subData.subscription);
        setIsSubscriptionActive(subData.isActive || false);

        if (!subData.isActive || !subData.subscription) {
          setLoading(false);
          return;
        }

        const allowedIds = getAllowedCourseIds(subData.subscription);
        const allCourses = coursesData.courses || [];
        const myCourses = allowedIds.length > 0
          ? allCourses.filter(c => allowedIds.includes(c._id.toString()))
          : allCourses;

        setCourses(myCourses);

        const classResults = await Promise.all(
          myCourses.map(c =>
            api.getClassesByCourse(c._id)
              .then(d => ({ courseId: c._id, classes: (d.classes || []).filter(cl => cl.status === 'published') }))
              .catch(() => ({ courseId: c._id, classes: [] }))
          )
        );
        const classMap = {};
        classResults.forEach(r => { classMap[r.courseId] = r.classes; });
        setCourseClasses(classMap);
      } catch (err) {
        console.error('Error fetching courses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getAllowedCourseIds = (sub) => {
    if (!sub?.allowedCourses) return [];
    return sub.allowedCourses.map(c => (c._id || c).toString());
  };

  const getUpcomingClassCount = (courseId) => {
    const now = new Date();
    return (courseClasses[courseId] || []).filter(c => new Date(c.scheduledDate) >= now).length;
  };

  const getNextClass = (courseId) => {
    const now = new Date();
    return (courseClasses[courseId] || [])
      .filter(c => new Date(c.scheduledDate) >= now)
      .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-16 text-dark-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-3" />
              Loading your courses...
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card p-10 text-center">
              <svg className="w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h2 className="text-xl font-bold text-dark-900 mb-2">No Active Subscription</h2>
              <p className="text-dark-500 mb-6">Enroll in a course to get started.</p>
              <Link to="/student/dashboard" className="btn-primary inline-block">Go to Dashboard</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-50">
      <SEO title="My Courses" noIndex />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <nav className="flex items-center gap-2 text-sm text-dark-400 mb-3">
              <Link to="/student/dashboard" className="hover:text-primary-500">Dashboard</Link>
              <span>/</span>
              <span className="text-dark-900">My Courses</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold text-dark-900 mb-1">My Courses</h1>
            <p className="text-dark-500">{courses.length} course{courses.length !== 1 ? 's' : ''} in your subscription</p>
          </div>

          {courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => {
                const nextClass = getNextClass(course._id);
                const upcomingCount = getUpcomingClassCount(course._id);
                const totalClasses = (courseClasses[course._id] || []).length;

                return (
                  <div key={course._id} className="card overflow-hidden border border-primary-100 flex flex-col">
                    <div className="relative">
                      <img
                        src={formatCourseThumbnail(course.thumbnail)}
                        alt={course.title}
                        className="w-full h-44 object-cover"
                        onError={handleThumbnailError}
                      />
                      <div className="absolute top-3 left-3 bg-primary-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        Subscribed
                      </div>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-lg font-semibold text-dark-900 mb-1 line-clamp-2">{course.title}</h3>
                      {course.teacher?.name && (
                        <p className="text-sm text-dark-500 mb-3">by {course.teacher.name}</p>
                      )}

                      {/* Next class highlight */}
                      {nextClass && (
                        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-3">
                          <p className="text-xs font-medium text-primary-700 mb-0.5">Next Class</p>
                          <p className="text-sm font-semibold text-dark-900 line-clamp-1">{nextClass.title}</p>
                          <p className="text-xs text-dark-500 mt-0.5">
                            {formatShortDate(nextClass.scheduledDate)}
                            {' at '}
                            {formatTime(nextClass.scheduledDate)}
                          </p>
                          {nextClass.meetingLink && (
                            <a href={nextClass.meetingLink} target="_blank" rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Join Class
                            </a>
                          )}
                        </div>
                      )}

                      {totalClasses > 0 && (
                        <p className="text-xs text-dark-400 mb-3">
                          {upcomingCount} upcoming · {totalClasses} total class{totalClasses !== 1 ? 'es' : ''}
                        </p>
                      )}

                      <div className="mt-auto flex gap-2">
                        <Link
                          to={`/courses/${course._id}`}
                          className="flex-1 btn-primary text-center text-sm py-2"
                        >
                          View Course & Classes
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <svg className="w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-lg font-semibold text-dark-900 mb-2">No courses found</h3>
              <p className="text-dark-500 mb-4">Your subscription doesn't include any courses yet.</p>
              <Link to="/student/dashboard" className="btn-primary inline-block">Go to Dashboard</Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentCourses;
